// The public site (platforma.life) marks favorite psychologists with hearts. It is another subdomain, so the cabinet
// shares a signed-in CLIENT's token with it in a cookie on the common parent domain; psychologists and admins are not shared.
// A heart pressed by a guest opens the cabinet with ?favorite=<profile id>&back=<site page>: after sign-in the psychologist
// is added and the client returns to that page.
import AppServices from "@/services/api/app.services";

const COOKIE = "pl_patient";
const PENDING_KEY = "pendingFavorite";

// dashboard.platforma.life + platforma.life -> platforma.life
function sharedDomain() {
  let site = "";
  try {
    site = new URL(process.env.VUE_APP_MAIN_PAGE).hostname;
  } catch (e) {
    return null;
  }
  const own = window.location.hostname.split(".").reverse();
  const other = site.split(".").reverse();
  const common = [];
  for (let i = 0; i < Math.min(own.length, other.length) && own[i] === other[i]; i++) {
    common.push(own[i]);
  }
  return common.length >= 2 ? common.reverse().join(".") : null;
}

function writeCookie(value, maxAge) {
  const domain = sharedDomain();
  if (!domain) return;
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${COOKIE}=${value}; domain=${domain}; path=/; max-age=${maxAge}; samesite=lax${secure}`;
}

let lastShared = null;

export function syncSharedAuth(state) {
  const token = state.user.authToken;
  const role = state.user.user && state.user.user.role;
  const value = state.user.isAuth && token && role === "patient" ? token : "";
  if (value === lastShared) return;
  lastShared = value;
  if (value) {
    writeCookie(encodeURIComponent(value), 60 * 60 * 24 * 30);
  } else {
    writeCookie("", 0);
  }
}

let pending;

function readPending() {
  if (pending === undefined) {
    try {
      pending = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    } catch (e) {
      pending = null;
    }
  }
  return pending;
}

// the page was opened from the site: a heart (/auth?favorite=<id>&back=<url>),
// "sign in and come back" (/auth?return=1&back=<url>, e.g. to ask on the forum)
// or "Написати" on a psychologist's page (/auth?chat=<profile id>: the chat opens after sign-in)
export function rememberPendingFavorite(search) {
  const query = new URLSearchParams(search);
  const id = query.get("favorite");
  const validId = id && /^[0-9a-f-]{36}$/.test(id);
  const chat = query.get("chat");
  const validChat = chat && /^[0-9a-f-]{36}$/.test(chat);
  if (!validId && !validChat && !(query.get("return") === "1" && query.get("back"))) return;
  pending = { id: validId ? id : null, chat: validChat ? chat : null, back: query.get("back"), at: Date.now() };
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch (e) {
    // no storage: works within this page load only
  }
}

// called on every store change; adds the psychologist once the client is signed in
export function applyPendingFavorite(state) {
  const item = readPending();
  if (!item || !state.user.isAuth || !state.user.user || !state.user.user.role) return false;

  pending = null;
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // nothing to clean
  }
  // an hour-old intent or a psychologist's account: forget it
  if (state.user.user.role !== "patient" || Date.now() - item.at > 60 * 60 * 1000) return false;

  if (item.chat) {
    window.location.href = "/dashboard/chat?to=" + encodeURIComponent(item.chat);
    return true;
  }

  const back = safeBack(item.back);
  if (!item.id) {
    if (back) window.location.href = back;
    return !!back;
  }
  AppServices.post("addFavorite", { id: item.id })
    .catch(() => {})
    .finally(() => {
      if (back) window.location.href = back;
    });
  return true;
}

// only back to our own site
function safeBack(url) {
  if (!url) return null;
  try {
    const target = new URL(url);
    const site = new URL(process.env.VUE_APP_MAIN_PAGE);
    return target.origin === site.origin ? target.href : null;
  } catch (e) {
    return null;
  }
}

// a guest's hearts on the site live in the pl_favorites cookie (same parent domain): once a client signs in here,
// they move to the account and the cookie is cleared
const GUEST_COOKIE = "pl_favorites";
let guestMoving = false;

export function applyGuestFavorites(state) {
  if (guestMoving || !state.user.isAuth || !state.user.user || state.user.user.role !== "patient") return;
  const match = document.cookie.match(new RegExp("(?:^|; )" + GUEST_COOKIE + "=([^;]*)"));
  const ids = (match && match[1] ? decodeURIComponent(match[1]) : "").split(",").filter((id) => /^[0-9a-f-]{36}$/.test(id));
  if (!ids.length) return;
  guestMoving = true;
  Promise.all(ids.map((id) => AppServices.post("addFavorite", { id }).catch(() => null))).then(() => {
    const domain = sharedDomain();
    const secure = window.location.protocol === "https:" ? "; secure" : "";
    document.cookie = `${GUEST_COOKIE}=${domain ? "; domain=" + domain : ""}; path=/; max-age=0; samesite=lax${secure}`;
    guestMoving = false;
  });
}
